import type { ReactElement } from 'react';
import type { SsoProviderId } from '@socialsoup/shared';
import { Button } from '@/components/ui/button';
import { useSsoProviders } from '@/features/auth/hooks/useSsoProviders';
import { safeNext } from '@/features/auth/lib/safe-next';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

// Provider marks keep the providers' own colours: their published sign-in button guidance
// requires the unaltered logo, so these fills are brand assets, not design-token colours.
function MicrosoftMark(): ReactElement {
  return (
    <svg
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 shrink-0"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function GoogleMark(): ReactElement {
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 shrink-0"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Fixed display order; a provider renders only when the API lists it. */
const PROVIDERS: ReadonlyArray<{ id: SsoProviderId; label: string; Mark: () => ReactElement }> = [
  { id: 'microsoft', label: 'Sign in with Microsoft', Mark: MicrosoftMark },
  { id: 'google', label: 'Sign in with Google', Mark: GoogleMark },
];

/**
 * `/api/v1/auth/sso/<provider>/start`, carrying `next` only when it is a safe internal path
 * other than the root (the API's own default). The API sanitises `next` again; this only keeps
 * an unsafe value out of the URL the browser sends.
 */
function ssoStartHref(provider: SsoProviderId, next: string | null): string {
  const target = safeNext(next);
  const query = target === '/' ? '' : `?next=${encodeURIComponent(target)}`;
  return `${API_BASE}/api/v1/auth/sso/${provider}/start${query}`;
}

interface SsoButtonsProps {
  /** The login page's `?next=`, raw; unsafe values are dropped. */
  next: string | null;
}

/**
 * "Sign in with Microsoft / Google" (sso-login Phase 4). Each button is a plain link, so the
 * sign-in is a full-page navigation to the API, never an XHR. Renders nothing — not even the
 * divider — when no provider is enabled or the provider list cannot be fetched.
 */
export function SsoButtons({ next }: SsoButtonsProps) {
  const enabled = useSsoProviders();
  const providers = PROVIDERS.filter((p) => enabled.includes(p.id));
  if (providers.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="space-y-2">
        {providers.map(({ id, label, Mark }) => (
          <Button key={id} asChild variant="outline" className="h-11 w-full gap-3">
            <a href={ssoStartHref(id, next)}>
              <Mark />
              {label}
            </a>
          </Button>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-3">
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
        <span className="text-xs text-content-secondary">or</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>
    </div>
  );
}
